"use client";
import { motion } from "framer-motion";
import { useState, useEffect, useRef } from "react";

const defaultImages = [
  "/home/item-1copy.webp",
  "/home/Item-2.webp",
  "/home/Item-3.webp",
  "/home/Item-4.webp",
  "/home/Item-5.webp",
  "/home/Item-6.webp",
  "/home/Item-7.webp",
  "/home/Item-8.webp",
];

const STACK_OFFSET = 12;
const STACK_SCALE = 0.05;
const STACK_ROTATION = 3;

export default function SwipeCards({ images = defaultImages }) {
  const [current, setCurrent] = useState(0);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const requested = useRef(new Set());
  const total = images.length;

  const loadImage = (src) => {
    if (!src || requested.current.has(src)) return;
    requested.current.add(src);
    const img = new Image();
    img.src = src;
    img.onload = () => setLoadedImages((prev) => new Set([...prev, src]));
  };

  // Preload first 2 images on mount / when image set changes
  useEffect(() => {
    loadImage(images[0]);
    loadImage(images[1]);
  }, [images]);

  // Lazy-load adjacent images on each swipe
  useEffect(() => {
    loadImage(images[(current + 1) % total]);
    loadImage(images[(current - 1 + total) % total]);
  }, [current, total, images]);

  const handleSwipe = (direction) => {
    if (direction === "left") {
      setCurrent((prev) => (prev + 1) % total);
    } else {
      setCurrent((prev) => (prev - 1 + total) % total);
    }
  };

  return (
    <div
      className="gutter-x py-16 pb-24 md:pb-16 px-4 md:px-0 overflow-hidden font-fira-sans"
      style={{
        borderLeft: "1px solid #08090A33",
        borderRight: "1px solid #08090A33",
      }}
    >
      <div className="mb-12">
        <p className="text-black text-[16px] tracking-[1.75px] uppercase mb-2">
          HANDS-ON WITH REAL TOOL
        </p>
        <h2 className="text-black text-[30px] md:text-[38px] font-medium">
          Master Tools of the Trade
        </h2>
      </div>

      <div className="relative w-[75%] max-w-sm mx-auto h-[310px]">
        {images.map((card, index) => {
          // Circular offset math
          let offset = index - current;

          if (offset > total / 2) offset -= total;
          if (offset < -total / 2) offset += total;

          const abs = Math.abs(offset);

          // Only ONE previous card visible
          if (offset < -1) return null;

          const isActive = offset === 0;

          return (
            <motion.div
              key={card}
              drag={isActive ? "x" : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={(e, info) => {
                if (info.offset.x < -80) handleSwipe("left");
                if (info.offset.x > 80) handleSwipe("right");
              }}
              animate={{
                x:
                  offset === 0
                    ? 0
                    : offset === -1
                    ? -STACK_OFFSET
                    : offset * STACK_OFFSET,

                y:
                  offset === 0
                    ? 0
                    : offset === -1
                    ? STACK_OFFSET * 0.6
                    : offset * STACK_OFFSET * 0.6,

                scale:
                  offset === 0
                    ? 1
                    : offset === -1
                    ? 1 - STACK_SCALE
                    : 1 - offset * STACK_SCALE,

                rotate:
                  offset === 0
                    ? 0
                    : offset === -1
                    ? -STACK_ROTATION
                    : offset * STACK_ROTATION,
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
              }}
              className="absolute w-full h-full rounded-[4px] shadow-xl bg-white"
              style={{
                backgroundImage: loadedImages.has(card) ? `url(${card})` : 'none',
                backgroundSize: "cover",
                backgroundPosition: "center",
                zIndex: 40 - abs,
                backgroundColor: loadedImages.has(card) ? 'transparent' : '#f0f0f0', // Placeholder while loading
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
